import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import { Box, Dialog, FormControl, InputLabel, Select, MenuItem, Button, Grid,
  TextField, FormControlLabel, Switch, InputAdornment, Checkbox } from '@mui/material';
import ReviewHighlights from './ReviewHighlights';
import ReviewTips from './ReviewTips';
import CircularProgress from '@mui/material/CircularProgress';
import FrameProcessor from './FrameProcessor';
import { httpsCallable } from 'firebase/functions';
import UserContext from './UserContext';
import { toast } from './toast';
import { ranges, suspectRanges } from './metricRanges';

const createNumberInputGrid = (label, value, min, handleChange, gridSize) => {
  return (
    <Grid item md={gridSize}>
      {createNumberInput(label, value, min, handleChange)}
    </Grid>
  );
};

const LABEL_TO_METRIC = {
  'Foot Strike': 'footStrikeAngle',
  'Tibia Angle': 'tibiaAngle',
  'Overstride': 'overstride',
  'Knee Flexion': 'kneeFlexion',
  'Heel Height': 'heelHeight',
  'Vertical Displacement': 'verticalDisplacement',
  'Trunk Lean': 'trunkLean',
  'Hip Extension': 'hipExtension',
  'Pelvic Drop': 'pelvicDrop',
  'Heel Eversion': 'heelEversion',
  'Toe Out': 'toeOut',
  'Heel Whip': 'heelWhip',
  'Base of Support': 'baseOfSupport',
  'Knee Window': 'kneeWindow',
  'Cadence': 'cadence',
};

const isValueSuspect = (label, value) => {
  if (value === undefined || value === '') return false;
  const val = parseFloat(value);
  const labelKey = Object.keys(LABEL_TO_METRIC).find(k => label.includes(k));
  if (!labelKey) return false;
  const suspect = suspectRanges[LABEL_TO_METRIC[labelKey]];
  if (!suspect) return false;
  return val < suspect.lower || val > suspect.upper;
};

const createNumberInput = (label, value, min, handleChange, required = true) => {
  const isSuspect = isValueSuspect(label, value);
  return (
    <FormControl key={label} fullWidth variant="outlined">
      <TextField
        type="number"
        size="small"
        sx={{ mt: 1 }}
        label={label}
        value={value}
        onChange={handleChange}
        required={required}
        inputProps={{ min }}
        InputLabelProps={{ shrink: value !== '' && value !== undefined && value !== null }}
        onWheel={(e) => e.target.blur()}
        variant="outlined"
        color={isSuspect ? 'warning' : 'primary'}
        focused={isSuspect}
      />
    </FormControl>
  );
}


const SkeletonFrame = ({ imageUrl, landmarks, phase, onImageClick }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const drawSkeleton = useCallback(() => {
    if (!canvasRef.current || !imgRef.current) return;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!img.naturalWidth) return;
    canvas.width = img.clientWidth;
    canvas.height = img.clientHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!landmarks) return;

    const w = canvas.width, h = canvas.height;
    const pt = (name) => {
      const l = landmarks[name];
      if (!l || l.v < 0.3) return null;
      return [l.x * w, l.y * h];
    };
    const midPt = (a, b) => (a && b) ? [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] : null;
    const drawLine = (from, to, color, lw = 2) => {
      if (!from || !to) return;
      ctx.beginPath(); ctx.moveTo(...from); ctx.lineTo(...to);
      ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.stroke();
    };
    const drawDot = (p, color, r = 4) => {
      if (!p) return;
      ctx.beginPath(); ctx.arc(...p, r, 0, Math.PI * 2);
      ctx.fillStyle = color; ctx.fill();
    };

    const lHip = pt('l_hip'), rHip = pt('r_hip');
    const lSho = pt('l_sho'), rSho = pt('r_sho');
    const lKne = pt('l_kne'), rKne = pt('r_kne');
    const lAnk = pt('l_ank'), rAnk = pt('r_ank');
    const lHeel = pt('l_heel'), rHeel = pt('r_heel');
    const lFoot = pt('l_foot'), rFoot = pt('r_foot');
    const hipC = midPt(lHip, rHip);
    const shoC = midPt(lSho, rSho);

    const grey = 'rgba(200,200,200,0.55)';
    drawLine(lSho, rSho, grey); drawLine(lHip, rHip, grey);
    drawLine(shoC, hipC, grey);
    drawLine(lHip, lKne, grey); drawLine(lKne, lAnk, grey); drawLine(lAnk, lHeel, grey); drawLine(lHeel, lFoot, grey);
    drawLine(rHip, rKne, grey); drawLine(rKne, rAnk, grey); drawLine(rAnk, rHeel, grey); drawLine(rHeel, rFoot, grey);

    const stance = landmarks._stance;
    if (!stance) return;
    const sp = stance === 'left' ? 'l_' : 'r_';
    const sw = stance === 'left' ? 'r_' : 'l_';
    const stKne = pt(`${sp}kne`), stAnk = pt(`${sp}ank`), stHip = pt(`${sp}hip`);
    const stHeel = pt(`${sp}heel`), stFoot = pt(`${sp}foot`);
    const swKne = pt(`${sw}kne`), swAnk = pt(`${sw}ank`);
    const orange = '#FF6B2B', blue = '#4DA8FF', green = '#4DFF9C';

    if (phase === 'LR') {
      drawLine(stKne, stAnk, orange, 3);
      drawDot(stKne, orange); drawDot(stAnk, orange);
      drawLine(stHeel, stFoot, green, 3);
      drawLine(hipC, stAnk, blue, 2);
      drawDot(hipC, blue, 5);
    } else if (phase === 'MSt') {
      drawLine(stHip, stKne, orange, 3); drawLine(stKne, stAnk, orange, 3);
      drawDot(stHip, orange); drawDot(stKne, orange); drawDot(stAnk, orange);
      drawLine(swKne, swAnk, blue, 3);
      drawDot(swKne, blue); drawDot(swAnk, blue);
    } else if (phase === 'LSt') {
      drawLine(shoC, hipC, orange, 3);
      drawDot(shoC, orange, 5); drawDot(hipC, orange, 5);
      drawLine(stKne, stHip, blue, 3); drawLine(stHip, shoC, blue, 3);
      drawDot(stKne, blue); drawDot(stHip, blue);
    }
  }, [landmarks, phase]);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth) drawSkeleton();
  }, [drawSkeleton]);

  if (!imageUrl) return null;
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <img
        ref={imgRef}
        src={imageUrl}
        alt="Video frame"
        style={{ maxHeight: '400px', cursor: 'pointer', display: 'block' }}
        onLoad={drawSkeleton}
        onClick={() => onImageClick && onImageClick(imageUrl)}
      />
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      />
    </div>
  );
};


const ReviewSubmissionValidForm = ({
  submissionId,
  userId,
  videoRef,
  cropRegion,
  videoAngle,
  setVideoAngle,
  timestampImages,
  setTimestampImages,
  timestamps,
  setTimestamps,
  onProcessingSuccess,
  processingDone,
  data,
  setData,
  handleValid,
  highlights,
  setHighlights,
  tips,
  setTips,
  isUploading,
  goal,
  symptoms,
  isSprinter,
  setIsSprinter,
  poseLandmarks,
  pipelineTiming,
}) => {
  const { functions } = useContext(UserContext);
  const R = ranges(isSprinter);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [adminHint, setAdminHint] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [rotation, setRotation] = useState('');
  const [skipCrop, setSkipCrop] = useState(false);
  const [proofreadingIndex, setProofreadingIndex] = useState(null);

  const prepareImages = async () => {
    const viewImages = timestampImages[videoAngle];
    const result = [];

    const processUrl = async (url, label) => {
      if (!url) return;
      if (url.startsWith('data:')) {
        const commaIdx = url.indexOf(',');
        const mimeType = url.slice(5, url.indexOf(';'));
        result.push({ mimeType, data: url.slice(commaIdx + 1), label });
      } else if (url.startsWith('http')) {
        try {
          const resp = await fetch(url);
          const blob = await resp.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
          result.push({ mimeType: blob.type || 'image/jpeg', data: btoa(binary), label });
        } catch (e) {
          // skip image if fetch fails
        }
      }
    };

    if (videoAngle === 'side') {
      for (const [phase, url] of Object.entries(viewImages)) {
        await processUrl(url, phase);
      }
    } else {
      for (const [phase, legs] of Object.entries(viewImages)) {
        for (const [leg, url] of Object.entries(legs)) {
          await processUrl(url, `${phase} ${leg}`);
        }
      }
    }
    return result;
  };

  const handleProofreadTip = async (index) => {
    const tip = tips[index];
    if (!tip.description?.trim()) return;
    setProofreadingIndex(index);
    try {
      const proofreadFn = httpsCallable(functions, 'proofreadTip', { timeout: 30000 });
      const response = await proofreadFn({ description: tip.description });
      const newTips = [...tips];
      newTips[index] = { ...newTips[index], description: response.data.description };
      setTips(newTips);
      toast.success('Tip proofread!');
    } catch (err) {
      console.error('Proofread error:', err);
      toast.error(err.message || 'Failed to proofread tip.');
    } finally {
      setProofreadingIndex(null);
    }
  };

  const handleGenerateTips = async () => {
    setIsGenerating(true);
    try {
      const images = await prepareImages();
      const metrics = videoAngle === 'side' ? data.side : data.back;
      const generateTipsFn = httpsCallable(functions, 'generateTips', { timeout: 120000 });
      const response = await generateTipsFn({
        hint: adminHint,
        metrics,
        cadence: data.cadence,
        highlights,
        videoAngle,
        goal,
        symptoms,
        images,
      });
      setTips(response.data.tips);
      toast.success('Tips generated!');
    } catch (err) {
      console.error('Generate tips error:', err);
      toast.error(err.message || 'Failed to generate tips.');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderImage = (imageUrl) => {
    return (
      imageUrl && (
        <img
          src={imageUrl}
          alt="Video frame"
          style={{ maxHeight: "400px", cursor: "pointer" }}
          onClick={() => {
            setCurrentImage(imageUrl);
            setOpenImageModal(true);
          }}
        />
      )
    );
  };

  const handleTimestampChange = (view, phase, leg = null) => (event) => {
    event.preventDefault();

    const vw = videoRef.current.videoWidth;
    const vh = videoRef.current.videoHeight;
    const srcX = Math.round(cropRegion.x * vw);
    const srcY = Math.round(cropRegion.y * vh);
    const srcW = Math.round(cropRegion.w * vw);
    const srcH = Math.round(cropRegion.h * vh);

    const canvas = document.createElement('canvas');
    canvas.width = srcW;
    canvas.height = srcH;
    const context = canvas.getContext('2d');
    context.drawImage(videoRef.current, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
    const imageUrl = canvas.toDataURL('image/webp', 0.8);
    const currentTime = videoRef.current.currentTime;

    setTimestampImages((prevImages) => ({
      ...prevImages,
      [view]: {
        ...prevImages[view],
        [phase]: leg ? { ...prevImages[view][phase], [leg]: imageUrl } : imageUrl,
      },
    }));

    setTimestamps((prevTimestamps) => ({
      ...prevTimestamps,
      [view]: {
        ...prevTimestamps[view],
        [phase]: leg ? { ...prevTimestamps[view][phase], [leg]: currentTime } : currentTime,
      },
    }));
  };

  const areTimestampsSet = () => {
    if (videoAngle === 'side') {
      return ['LR', 'MSt', 'LSt', 'MSw'].every(
        (phase) => timestampImages.side[phase] !== null && timestampImages.side[phase] !== ''
      );
    }
    if (videoAngle === 'back') {
      return ['MSt', 'MSw', 'IC'].every((phase) =>
        ['left', 'right'].every(
          (leg) => timestampImages.back[phase][leg] !== null && timestampImages.back[phase][leg] !== ''
        )
      );
    }
    return false;
  };

  const handleDataChange = (view, phase, parameter, leg = null) => (event) => {
    const rawValue = event.target.value;
    const value = isNaN(parseFloat(rawValue)) ? rawValue : parseFloat(rawValue);
    if (view === 'side') {
      setData((prevState) => ({
        ...prevState,
        side: {
          ...prevState.side,
          [phase]: {
            ...prevState.side[phase],
            [parameter]: value,
          },
        },
      }));
    } else if (view === 'back') {
      setData((prevState) => {
        if (leg !== null) { // When leg-specific data needs to be updated
          return {
            ...prevState,
            back: {
              ...prevState.back,
              [phase]: {
                ...prevState.back[phase],
                [parameter]: {
                  ...prevState.back[phase][parameter],
                  [leg]: value,
                },
              },
            },
          };
        } else { // When data is not leg-specific (baseOfSupport, kneeWindow)
          return {
            ...prevState,
            back: {
              ...prevState.back,
              [phase]: {
                ...prevState.back[phase],
                [parameter]: value,
              },
            },
          };
        }
      });
    }
  };

  const handleCadenceChange = (event) => {
    const value = event.target.value;
    if (value !== '') {
      setData((prevState) => ({
        ...prevState,
        cadence: Math.max(0, value),
      }));
    } else {
      setData((prevState) => ({
        ...prevState,
        cadence: undefined,
      }));
    }
  };

  const handleTreadmillShakingChange = (event) => {
    const value = event.target.value;
    if (value !== '') {
      setData((prevState) => ({
        ...prevState,
        treadmillShakingDegree: Math.max(0, Math.min(5, value)),
      }));
    } else {
      setData((prevState) => ({
        ...prevState,
        treadmillShakingDegree: undefined,
      }));
    }
  };

  return (
    <form onSubmit={handleValid}>
      <Box sx={{mt:1, mb:1}} >
        <FormControl size="small" variant="outlined" fullWidth>
          <InputLabel id="video-angle-label">Video Angle</InputLabel>
          <Select
            labelId="video-angle-label"
            value={videoAngle}
            onChange={(e) => setVideoAngle(e.target.value)}
            required
          >
            <MenuItem value="">
              <em>--Select a video angle--</em>
            </MenuItem>
            <MenuItem value="side">Side View</MenuItem>
            <MenuItem value="back">Back View</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Switch checked={isSprinter} onChange={(e) => setIsSprinter(e.target.checked)} />}
          label="Sprinter"
        />

        {videoAngle === 'side' && (
          <>
            <fieldset>
              <legend>Images</legend>
              <Box mb={1} display="flex" alignItems="center" gap={1}>
                <FrameProcessor
                  submissionId={submissionId}
                  userId={userId}
                  videoAngle={videoAngle}
                  timestampImages={timestampImages}
                  onSuccess={onProcessingSuccess}
                  disabled={!areTimestampsSet()}
                  rotation={rotation ? parseFloat(rotation) : 0}
                  skipCrop={skipCrop}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Rotate result"
                  value={rotation}
                  onChange={(e) => setRotation(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  sx={{ width: 140 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">°</InputAdornment> }}
                />
                <FormControlLabel
                  control={<Checkbox checked={skipCrop} onChange={(e) => setSkipCrop(e.target.checked)} size="small" />}
                  label="Do not crop"
                />
              </Box>
              <Grid container spacing={0.5}>
                {['LR', 'MSt', 'LSt', 'MSw'].map((phase) => (
                  <Grid item md={3} key={`${phase}-button`}>
                    <Button fullWidth variant="contained" color="primary" onClick={handleTimestampChange('side', phase)}>
                      Mark {phase} Time
                    </Button>
                  </Grid>
                ))}
                {['LR', 'MSt', 'LSt', 'MSw'].map((phase) => (
                  <Grid item md={3} key={`${phase}-image`}>
                    <SkeletonFrame
                      imageUrl={timestampImages.side[phase]}
                      landmarks={poseLandmarks?.[phase] || null}
                      phase={phase}
                      onImageClick={(url) => { setCurrentImage(url); setOpenImageModal(true); }}
                    />
                  </Grid>
                ))}
              </Grid>
              {pipelineTiming && (
                <Box mt={1} sx={{ fontSize: '0.75rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                  {Object.entries(pipelineTiming).map(([k, v]) => `${k}: ${v}ms`).join(' · ')}
                </Box>
              )}
            </fieldset>

            <fieldset>
              <legend>Side view metrics</legend>
              {createNumberInput(`Foot Strike, ${R.footStrikeAngle.asString()}, LR`, data.side.LR.footStrikeAngle, 0, handleDataChange('side', 'LR', 'footStrikeAngle'))}
              {createNumberInput(`Tibia Angle, ${R.tibiaAngle.asString()}, LR`, data.side.LR.tibiaAngle, 0, handleDataChange('side', 'LR', 'tibiaAngle'))}
              {createNumberInput(`Overstride, ${R.overstride.asString()}, LR`, data.side.LR.degreeOfOverstriding, 0, handleDataChange('side', 'LR', 'degreeOfOverstriding'))}
              {createNumberInput(`Knee Flexion, ${R.kneeFlexion.asString()}, MSt`, data.side.MSt.kneeFlexionAngle, 0, handleDataChange('side', 'MSt', 'kneeFlexionAngle'))}
              {createNumberInput(`Heel Height, ${R.heelHeight.asString()}, MSt`, data.side.MSt.heelHeight, 0, handleDataChange('side', 'MSt', 'heelHeight'))}
              {createNumberInput(`Vertical Displacement, ${R.verticalDisplacement.asString()}, MSt-MSw`, data.side.MSt.verticalDisplacement, 0, handleDataChange('side', 'MSt', 'verticalDisplacement'))}
              {createNumberInput(`Trunk Lean, ${R.trunkLean.asString()}, LSt`, data.side.LSt.trunkLeanAngle, 0, handleDataChange('side', 'LSt', 'trunkLeanAngle'))}
              {createNumberInput(`Hip Extension, ${R.hipExtension.asString()}, LSt`, data.side.LSt.hipExtensionAngle, 0, handleDataChange('side', 'LSt', 'hipExtensionAngle'))}
            </fieldset>
          </>
        )}

        {videoAngle === 'back' && (
          <>
            <fieldset>
              <legend>Images</legend>
              <Box mb={1} display="flex" alignItems="center" gap={1}>
                <FrameProcessor
                  submissionId={submissionId}
                  userId={userId}
                  videoAngle={videoAngle}
                  timestampImages={timestampImages}
                  onSuccess={onProcessingSuccess}
                  disabled={!areTimestampsSet()}
                  rotation={rotation ? parseFloat(rotation) : 0}
                  skipCrop={skipCrop}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Rotate result"
                  value={rotation}
                  onChange={(e) => setRotation(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  sx={{ width: 140 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">°</InputAdornment> }}
                />
                <FormControlLabel
                  control={<Checkbox checked={skipCrop} onChange={(e) => setSkipCrop(e.target.checked)} size="small" />}
                  label="Do not crop"
                />
              </Box>
              <Grid container spacing={0.5}>
                {['left', 'right'].map((leg) =>
                  ['IC', 'MSt', 'MSw'].map((phase) => (
                    <Grid item md={4} key={`${leg}-${phase}-button`}>
                      <Button fullWidth variant="contained" color="primary" onClick={handleTimestampChange('back', phase, leg)}>
                        Mark {phase} Time, {leg}
                      </Button>
                    </Grid>
                  ))
                )}
                {['left', 'right'].map((leg) =>
                  ['IC', 'MSt', 'MSw'].map((phase) => (
                    <Grid item md={4} key={`${leg}-${phase}-image`}>
                      {renderImage(timestampImages.back[phase][leg])}
                    </Grid>
                  ))
                )}
              </Grid>
            </fieldset>
            <fieldset>
              <legend>Back view metrics</legend>
              <Grid container spacing={1}>
                {createNumberInputGrid(`Pelvic Drop, L, ${R.pelvicDrop.asString()}, IC vs MSt`, data.back.IC.pelvicDrop.left, 0, handleDataChange('back', 'IC', 'pelvicDrop', 'left'), 6)}
                {createNumberInputGrid(`Pelvic Drop, R, ${R.pelvicDrop.asString()}, IC vs MSt`, data.back.IC.pelvicDrop.right, 0, handleDataChange('back', 'IC', 'pelvicDrop', 'right'), 6)}

                {createNumberInputGrid(`Heel Eversion, L, ${R.heelEversion.asString()}, IC vs MSt`, data.back.IC.heelEversion.left, 0, handleDataChange('back', 'IC', 'heelEversion', 'left'), 6)}
                {createNumberInputGrid(`Heel Eversion, R, ${R.heelEversion.asString()}, IC vs MSt`, data.back.IC.heelEversion.right, 0, handleDataChange('back', 'IC', 'heelEversion', 'right'), 6)}

                {createNumberInputGrid(`Toe Out, L, ${R.toeOut.asString()}, MSt`, data.back.MSt.toeOutAngle.left, 0, handleDataChange('back', 'MSt', 'toeOutAngle', 'left'), 6)}
                {createNumberInputGrid(`Toe Out, R, ${R.toeOut.asString()}, MSt`, data.back.MSt.toeOutAngle.right, 0, handleDataChange('back', 'MSt', 'toeOutAngle', 'right'), 6)}

                {createNumberInputGrid(`Vertical Displacement, L, ${R.verticalDisplacement.asString()}, MSt vs MSw`, data.back.MSt.verticalDisplacement.left, 0, handleDataChange('back', 'MSt', 'verticalDisplacement', 'left'), 6)}
                {createNumberInputGrid(`Vertical Displacement, R, ${R.verticalDisplacement.asString()}, MSt vs MSw`, data.back.MSt.verticalDisplacement.right, 0, handleDataChange('back', 'MSt', 'verticalDisplacement', 'right'), 6)}

                {createNumberInputGrid(`Heel Height, L, ${R.heelHeight.asString()}, MSt`, data.back.MSt.heelHeight.left, 0, handleDataChange('back', 'MSt', 'heelHeight', 'left'), 6)}
                {createNumberInputGrid(`Heel Height, R, ${R.heelHeight.asString()}, MSt`, data.back.MSt.heelHeight.right, 0, handleDataChange('back', 'MSt', 'heelHeight', 'right'), 6)}

                {createNumberInputGrid(`Heel Whip, L, ${R.heelWhip.asString()}, MSw`, data.back.MSw.heelWhipAngle.left, 0, handleDataChange('back', 'MSw', 'heelWhipAngle', 'left'), 6)}
                {createNumberInputGrid(`Heel Whip, R, ${R.heelWhip.asString()}, MSw`, data.back.MSw.heelWhipAngle.right, 0, handleDataChange('back', 'MSw', 'heelWhipAngle', 'right'), 6)}

                {createNumberInputGrid(`Base of Support, ${R.baseOfSupport.asString()}, MSt`, data.back.MSt.baseOfSupport, 0, handleDataChange('back', 'MSt', 'baseOfSupport'), 12)}
                {createNumberInputGrid(`Knee Window, ${R.kneeWindow.asString()}, MSt`, data.back.MSt.kneeWindow, 0, handleDataChange('back', 'MSt', 'kneeWindow'), 12)}
              </Grid>
            </fieldset>
          </>
        )}

        {videoAngle && (
          <>
            <fieldset>
              <legend>Other metrics</legend>
              {createNumberInput(`Cadence, ${R.cadence.asString()} steps per min`, data.cadence || '', 0, handleCadenceChange)}
              <FormControl sx={{mt:1}} size="small" variant="outlined" fullWidth>
                <InputLabel id="treadmill-shaking-degree-label">Treadmill Shaking Degree</InputLabel>
                <Select
                  labelId="treadmill-shaking-degree-label"
                  value={data.treadmillShakingDegree || ''}
                  onChange={handleTreadmillShakingChange}
                >
                  <MenuItem value="">
                    <em>--Select a degree--</em>
                  </MenuItem>
                  {[0, 1, 2, 3, 4, 5].map((degree) => (
                    <MenuItem key={degree} value={degree}>{degree}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </fieldset>
            <ReviewHighlights highlights={highlights} setHighlights={setHighlights} />
            <Box sx={{ mt: 2, mb: 1 }}>
              <TextField
                label="Admin hint for AI (e.g. 'typical female runner with narrow knee window and knee pain')"
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={adminHint}
                onChange={(e) => setAdminHint(e.target.value)}
              />
              <Button
                sx={{ mt: 1 }}
                variant="outlined"
                color="primary"
                onClick={handleGenerateTips}
                disabled={!areTimestampsSet() || isGenerating}
                startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isGenerating ? 'Generating...' : 'Generate Tips with AI'}
              </Button>
            </Box>
            <ReviewTips tips={tips} setTips={setTips} onProofread={handleProofreadTip} proofreadingIndex={proofreadingIndex} />
          </>
        )}
        <Button sx={{mt:1}}
          variant="contained"
          color="primary"
          type="submit"
          disabled={!areTimestampsSet() || isUploading || !processingDone}
          fullWidth>
          {isUploading ? <CircularProgress size={24} /> : "Mark as Reviewed"}
        </Button>

        <Dialog
          open={openImageModal}
          onClose={() => setOpenImageModal(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{ style: { maxHeight: "100vh", maxWidth: "100vw", margin: 0 } }}
        >
          <img
            src={currentImage}
            alt="Zoomed video frame"
            style={{ maxWidth: "100%", maxHeight: "100vh", objectFit: "contain", display: "block" }}
          />
        </Dialog>

      </Box>
    </form>
  );
};

export default ReviewSubmissionValidForm;
